import { useState } from "react";
import type { OrderDetailsResponse } from "./types";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";
import toast from "react-hot-toast";
import { Loader, X, Check } from "lucide-react";
import AddressSelectionModal from "../common/AddressModal/AddressSelectionModal";

interface OrderDetailsHeaderProps {
  order: OrderDetailsResponse;
  onUpdateSuccess?: () => void;
}

const OrderDetailsHeader: React.FC<OrderDetailsHeaderProps> = ({ order, onUpdateSuccess }) => {
  const [showModal, setShowModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [patientData, setPatientData] = useState<any>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const handleOpenModal = async () => {
    setShowModal(true);
    try {
      setLoadingMembers(true);
      const res = await AdminOrderManagementService.getPatientAndFamilyDetails(order.patientId);
      if (res.success && res.data) {
        setPatientData(res.data);
        setSelectedId(order.orderedFor || res.data.patientDetails?.userId || "");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load family members");
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    try {
      setSaving(true);
      const res = await AdminOrderManagementService.updateOrderedFor(order.orderId, selectedId);
      if (res.success) {
        toast.success("Order recipient updated successfully!");
        setShowModal(false);
        if (onUpdateSuccess) onUpdateSuccess();
      } else {
        toast.error(res.message || "Failed to update recipient");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update recipient");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 p-4 mb-5">
      <h3 className="text-sm font-bold text-slate-800 mb-3">Patient Details</h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-xs">
        {/* Invoice Number */}
        <div>
          <div className="text-slate-400 font-semibold mb-1 text-[0.65rem] uppercase tracking-wide">Invoice Number</div>
          <div className="text-slate-800 font-bold">{order.orderNumber}</div>
        </div>
        {/* Order Date */}
        <div>
          <div className="text-slate-400 font-semibold mb-1 text-[0.65rem] uppercase tracking-wide">Order Date</div>
          <div className="text-slate-800 font-bold">{formatDate(order.orderDate)}</div>
        </div>
        {/* Ordered By */}
        <div>
          <div className="text-slate-400 font-semibold mb-1 text-[0.65rem] uppercase tracking-wide">Ordered By</div>
          <div className="flex items-center gap-2">
            {order.patientImage && (
              <img src={order.patientImage} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-200" />
            )}
            <div>
              <div className="text-slate-800 font-bold">{order.patientName}</div>
              <div className="text-slate-400 text-[0.6rem]">+91 {order.patientPhone}</div>
            </div>
          </div>
        </div>
        {/* Ordered For */}
        <div>
          <div className="text-slate-400 font-semibold mb-1 text-[0.65rem] uppercase tracking-wide flex items-center gap-1.5">
            <span>Ordered For</span>
            <button
              onClick={handleOpenModal}
              className="text-[0.6rem] text-emerald-600 hover:text-white bg-emerald-50 hover:bg-emerald-500 rounded px-1.5 py-0.5 border border-emerald-200 font-bold cursor-pointer transition-all shadow-sm"
            >
              Edit
            </button>
          </div>
          <div className="flex items-center gap-2">
            {order.orderForImage && (
              <img src={order.orderForImage} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-200" />
            )}
            <div>
              <div className="text-slate-800 font-bold">{order.orderedForName}</div>
              <div className="text-slate-400 text-[0.6rem]">+91 {order.orderedForPhone}</div>
            </div>
          </div>
        </div>
        {/* Clinic */}
        <div>
          <div className="text-slate-400 font-semibold mb-1 text-[0.65rem] uppercase tracking-wide">Clinic</div>
          <div>
            <div className="text-slate-800 font-bold">{order.clinicName}</div>
            <div className="text-slate-400 text-[0.6rem]">+91 {order.clinicPhone}</div>
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="mt-3 pt-3 border-t border-slate-200">
        <div className="text-slate-400 font-semibold mb-1 text-[0.65rem] uppercase tracking-wide flex items-center justify-between">
          <span>Delivery Address</span>
          <button
            onClick={() => setShowAddressModal(true)}
            className="text-[0.6rem] text-emerald-600 hover:text-white bg-emerald-50 hover:bg-emerald-500 rounded px-2 py-0.5 border border-emerald-200 font-bold cursor-pointer transition-all shadow-xs"
          >
            Edit Address
          </button>
        </div>
        <div className="text-xs text-slate-700 font-medium">
          {order.address}, {order.city}, {order.state} - {order.pincode}
        </div>
      </div>

      {/* Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200000] flex items-center justify-center animate-fadeIn" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl max-w-[500px] w-[95%] max-h-[80vh] flex flex-col shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-bold text-slate-800">Select Recipient (Ordered For)</h3>
              <button className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer" onClick={() => setShowModal(false)}>
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto p-5">
              {loadingMembers ? (
                <div className="flex items-center justify-center py-10">
                  <Loader size={24} className="animate-spin text-emerald-500" />
                </div>
              ) : patientData ? (
                <div className="flex flex-col gap-3">
                  <p className="text-xs text-slate-500 font-medium mb-1">
                    Select family member or primary patient who this order is for:
                  </p>

                  {/* Primary Patient */}
                  {patientData.patientDetails && (
                    <div
                      onClick={() => setSelectedId(patientData.patientDetails.userId)}
                      className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                        selectedId === patientData.patientDetails.userId
                          ? "border-emerald-500 bg-emerald-50/50 shadow-sm"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={patientData.patientDetails.profileImage || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"}
                          alt=""
                          className="w-9 h-9 rounded-full object-cover border border-slate-200"
                        />
                        <div>
                          <div className="text-xs font-bold text-slate-800">
                            {patientData.patientDetails.fullName} (Self)
                          </div>
                          <div className="text-[0.65rem] text-slate-400 font-semibold">
                            +91 {patientData.patientDetails.mobileNumber}
                          </div>
                        </div>
                      </div>
                      {selectedId === patientData.patientDetails.userId && (
                        <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                          <Check size={12} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Family Members */}
                  {patientData.familyMembers && patientData.familyMembers.length > 0 && (
                    <div className="flex flex-col gap-2 mt-2">
                      <div className="text-[0.65rem] font-bold text-slate-400 uppercase tracking-wide">
                        Family Members
                      </div>
                      {patientData.familyMembers.map((member: any) => (
                        <div
                          key={member.id}
                          onClick={() => setSelectedId(member.id)}
                          className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                            selectedId === member.id
                              ? "border-emerald-500 bg-emerald-50/50 shadow-sm"
                              : "border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <img
                              src={member.profileImage || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"}
                              alt=""
                              className="w-9 h-9 rounded-full object-cover border border-slate-200"
                            />
                            <div>
                              <div className="text-xs font-bold text-slate-800">
                                {member.fullName}
                              </div>
                              <div className="text-[0.65rem] text-slate-400 font-semibold">
                                {member.relation} • {member.gender} • {member.age} yrs
                              </div>
                            </div>
                          </div>
                          {selectedId === member.id && (
                            <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                              <Check size={12} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-400 text-center py-5">No patient details found</p>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50/50">
              <button
                className="px-4 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
                onClick={handleSave}
                disabled={saving || !selectedId}
              >
                {saving && <Loader size={12} className="animate-spin" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Address Selection Modal */}
      <AddressSelectionModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        patientId={order.patientId}
        orderId={order.orderId}
        currentAddress={order.address}
        currentPincode={order.pincode}
        currentClinicId={order.clinicId}
        onAddressUpdated={() => {
          if (onUpdateSuccess) onUpdateSuccess();
        }}
      />
    </div>
  );
};

export default OrderDetailsHeader;
